import { createSelector } from '@reduxjs/toolkit';
import { isEmptyObj } from '../utilities/common';
import { KNOWN_UXSETTINGS } from '../constants/user';

const ADMIN_ROLES = ['ccaadmin', 'cca'];

export const getAdviserAtFirmLocationId = (state) =>
  state.user.adviserAtFirmLocationId;

export const getUserAccount = (state) => {
  try {
    return state.user.authentication.account;
  } catch {
    return null;
  }
};

export const getUXSettings = (state) => {
  try {
    return state.user.uxSettings.settings;
  } catch {
    return null;
  }
};

export const getUXSetting = createSelector(
  [getUXSettings, (state, key) => key],
  (settings, key) => {
    try {
      return settings[key];
    } catch {
      return null;
    }
  }
);

export const isCcaOrCcaAdmin = (state) => {
  try {
    return ADMIN_ROLES.includes(
      state.user.authentication?.account?.idTokenClaims?.extension_GryphonRoles?.toLowerCase()
    );
  } catch {
    return false;
  }
};

export const getMarketingAlerts = (state) => {
  try {
    if (!isEmptyObj(state.user.marketingAlert)) {
      return state.user.marketingAlert;
    }
    return null;
  } catch {
    return null;
  }
};

export const getIsIndemCommissionAllowed = (state) => {
  try {
    return state.user.uxSettings.isIndemCommissionAllowed;
  } catch {
    return null;
  }
};

export const getUserToken = (state) => {
  try {
    return state.user.authentication.token;
  } catch {
    return null;
  }
};

export const getUserError = (state) => {
  try {
    return state.user.error;
  } catch {
    return null;
  }
};

export const isLifeRangeHidden = (state, value) => {
  try {
    if (state.user.uxSettings?.settings?.[value]) {
      return true;
    }
  } catch {
    return false;
  }
  return false;
};

export const getLifeProtectionHidden = (state) => {
  return isLifeRangeHidden(state, KNOWN_UXSETTINGS.HIDE_LIFE_PROTECTION);
};

export const getLifeEssentialsHidden = (state) => {
  return isLifeRangeHidden(state, KNOWN_UXSETTINGS.HIDE_LIFE_ESSENTIALS);
};

export const getCiEssentialsHidden = (state) => {
  return isLifeRangeHidden(state, KNOWN_UXSETTINGS.HIDE_CI_ESSENTIALS);
};

export const getCiProtectionHidden = (state) => {
  return isLifeRangeHidden(state, KNOWN_UXSETTINGS.HIDE_CI_PROTECTION);
};

export const getLifeEssentialsAndProtectionHidden = (state) => {
  return getLifeProtectionHidden(state) && getLifeEssentialsHidden(state);
};

export const hasFixedIndemnityPercent = (state) => {
  try {
    if (
      state.user.uxSettings?.settings &&
      !Number.isNaN(state.user.uxSettings?.settings?.fixedIndemnityPercentage)
    ) {
      return true;
    }
  } catch {
    return false;
  }
  return false;
};

export const getFixedIndemnityPercent = (state) => {
  if (hasFixedIndemnityPercent(state)) {
    return state.user.uxSettings?.settings?.fixedIndemnityPercentage;
  }
  return null;
};

export const getIndemnityCommissionOnly = (state) => {
  return (
    hasFixedIndemnityPercent(state) && getFixedIndemnityPercent(state) === 100
  );
};

export const getHybridCommissionOnly = (state) => {
  return (
    hasFixedIndemnityPercent(state) &&
    getFixedIndemnityPercent(state) > 0 &&
    getFixedIndemnityPercent(state) < 100
  );
};

export const getNonIndemnityCommissionOnly = (state) => {
  return (
    hasFixedIndemnityPercent(state) && getFixedIndemnityPercent(state) === 0
  );
};

export const FIXED_COMMISSION_TYPES = {
  INDEMNITY_COMMISSION: 'indemnity_commission',
  HYBRID_COMMISSION: 'hybrid_commission',
  NON_INDEMNITY_COMMISSION: 'non_indemnity_commission',
  NONE: 'none',
};

export const getFixedCommissionType = (state) => {
  if (getIsIndemCommissionAllowed(state) === false) {
    return FIXED_COMMISSION_TYPES.NONE;
  }
  if (getIndemnityCommissionOnly(state)) {
    return FIXED_COMMISSION_TYPES.INDEMNITY_COMMISSION;
  }
  if (getHybridCommissionOnly(state)) {
    return FIXED_COMMISSION_TYPES.HYBRID_COMMISSION;
  }
  if (getNonIndemnityCommissionOnly(state)) {
    return FIXED_COMMISSION_TYPES.NON_INDEMNITY_COMMISSION;
  }
  return FIXED_COMMISSION_TYPES.NONE;
};
